import { useCallback, useEffect, useState } from "react";
import type { NavShellKey } from "./types";

// ── Persisted sidebar state ───────────────────────────────────────────────
// The collapsed rail is a per-browser preference shared by every shell; the
// open/closed state of collapsible groups is kept per shell. Storage can be
// unavailable (private mode, blocked site data), so every access is guarded.

export const COLLAPSED_KEY = "nav.collapsed";
export function groupsKey(shellKey: NavShellKey): string {
  return `nav.groups.${shellKey}`;
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* preference is best effort */
  }
}

function readGroups(key: string): Record<string, boolean> {
  const raw = readStorage(key);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "boolean") out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export interface SidebarState {
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
  toggleCollapsed: () => void;
  /** A group the person has not opened or closed takes its own default (NavItem.defaultOpen, open if unset). */
  isGroupOpen: (groupId: string, defaultOpen?: boolean) => boolean;
  toggleGroup: (groupId: string, defaultOpen?: boolean) => void;
  setGroupOpen: (groupId: string, open: boolean) => void;
}

export function useSidebarState(shellKey: NavShellKey): SidebarState {
  const [collapsed, setCollapsedState] = useState<boolean>(() => readStorage(COLLAPSED_KEY) === "1");
  const [groups, setGroups] = useState<Record<string, boolean>>(() => readGroups(groupsKey(shellKey)));

  // A shell change (client -> read-through) swaps in that shell's group state.
  useEffect(() => {
    setGroups(readGroups(groupsKey(shellKey)));
  }, [shellKey]);

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    writeStorage(COLLAPSED_KEY, next ? "1" : null);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsedState((prev) => {
      const next = !prev;
      writeStorage(COLLAPSED_KEY, next ? "1" : null);
      return next;
    });
  }, []);

  const isGroupOpen = useCallback((groupId: string, defaultOpen = true) => groups[groupId] ?? defaultOpen, [groups]);

  const setGroupOpen = useCallback((groupId: string, open: boolean) => {
    setGroups((prev) => {
      const next = { ...prev, [groupId]: open };
      writeStorage(groupsKey(shellKey), JSON.stringify(next));
      return next;
    });
  }, [shellKey]);

  const toggleGroup = useCallback((groupId: string, defaultOpen = true) => {
    setGroups((prev) => {
      const next = { ...prev, [groupId]: !(prev[groupId] ?? defaultOpen) };
      writeStorage(groupsKey(shellKey), JSON.stringify(next));
      return next;
    });
  }, [shellKey]);

  return { collapsed, setCollapsed, toggleCollapsed, isGroupOpen, toggleGroup, setGroupOpen };
}
